import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

// ──────────────────────────────────────────────
// ProtectedRoute — Redirects to /login if not authenticated.
// Shows a loading skeleton while the auth check is in progress.
// ──────────────────────────────────────────────

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function LoadingSkeleton() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        {/* Brand mark */}
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-lg font-bold text-white">
          G
        </div>
        {/* Animated dots */}
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 animate-bounce rounded-full bg-indigo-400 [animation-delay:0ms]" />
          <div className="h-2 w-2 animate-bounce rounded-full bg-indigo-400 [animation-delay:150ms]" />
          <div className="h-2 w-2 animate-bounce rounded-full bg-indigo-400 [animation-delay:300ms]" />
        </div>
        <p className="text-sm text-slate-500">Cargando...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, token } = useAuthStore();
  const location = useLocation();

  if (token && !isAuthenticated) {
    return <LoadingSkeleton />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

ProtectedRoute.displayName = 'ProtectedRoute';

export { ProtectedRoute };
